import { useEffect, useMemo, useRef } from 'react';
import '../Styles/TradeSignalsSection.css';
import useStrategyDashboardStore from '../Stores/strategyDashboardStore';
import { playTradeAlarm } from '../../utils/alarmService';
import { formatEnumLabel, formatRunMode } from '../../utils/displayLabels';

const STRATEGY_META = {
  argos: {
    name: 'ARGOS',
    description: 'Adaptive Regime Guided Option Selling',
    postTime: '9:31 AM',
  },
  gemic: {
    name: 'GEMIC',
    description: 'Gamma Expected Move Iron Condor',
    postTime: '3:55 PM',
  },
  acce: {
    name: 'ACCE',
    description: 'Adaptive Capital & Convexity Engine',
    postTime: '3:55 PM',
  },
};

function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined || value === '') return '—';

  const number = Number(value);
  if (Number.isNaN(number)) return String(value);

  return number.toFixed(decimals);
}

function formatStatusLabel(status) {
  if (!status) return 'Waiting';

  switch (status) {
    case 'POSTED':
      return 'Posted';
    case 'NO_TRADE':
    case 'NO_TRADE_FILTERED':
      return 'No Trade';
    case 'WAITING':
      return 'Waiting';
    case 'ACTIVE_ALREADY':
      return 'Active';
    case 'POSTED_DETAILS_PENDING':
      return 'Pending';
    case 'ERROR':
      return 'Error';
    default:
      return formatEnumLabel(status);
  }
}

function formatTradeDescription(value) {
  if (!value) return 'No signal details available.';

  let text = String(value);

  text = text.replace(
    /\bRun mode:\s*([A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+)\b\.?/gi,
    (_, runMode) => {
      const formattedRunMode = formatRunMode(runMode);

      if (formattedRunMode === 'No Trade') {
        return '';
      }

      return `Run mode: ${formattedRunMode}.`;
    }
  );

  text = text.replace(
    /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g,
    match => formatEnumLabel(match)
  );

  text = text.replace(/\s+\./g, '.');
  text = text.replace(/\s+/g, ' ').trim();

  return text || 'No signal details available.';
}

function parseDateOnlyLocal(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseDateOnlyLocal(value);

  if (!date) return '—';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function getExpirationDate(signal) {
  const trade = signal?.trade;

  return (
    trade?.expiration_date ||
    signal?.expiration_date ||
    signal?.run?.expiration ||
    signal?.run?.expiration_day ||
    signal?.run?.em_expiration ||
    signal?.run?.chain_expiration ||
    null
  );
}

function getStatusType(status) {
  switch (status) {
    case 'POSTED':
      return 'posted';
    case 'NO_TRADE':
    case 'NO_TRADE_FILTERED':
      return 'noTrade';
    case 'WAITING':
      return 'waiting';
    case 'ACTIVE_ALREADY':
      return 'active';
    case 'POSTED_DETAILS_PENDING':
      return 'waiting';
    case 'ERROR':
      return 'error';
    default:
      return 'waiting';
  }
}

function getTradeLabel(signal) {
  if (!signal) return 'Next Signal';

  const expirationDate = getExpirationDate(signal);
  const opexLabel = expirationDate ? ` — OPEX ${formatDate(expirationDate)}` : '';

  switch (signal.status) {
    case 'POSTED':
      return `Latest Trade${opexLabel}`;
    case 'NO_TRADE':
    case 'NO_TRADE_FILTERED':
      return 'Today';
    case 'ACTIVE_ALREADY':
      return `Status${opexLabel}`;
    case 'ERROR':
      return 'Run Error';
    default:
      return 'Next Signal';
  }
}

function buildStats(strategyKey, signal) {
  const trade = signal?.trade;

  if (!trade) return [];

  const stats = [];

  if (trade.entry_credit !== null && trade.entry_credit !== undefined) {
    stats.push({
      label: 'Credit',
      value: formatNumber(trade.entry_credit),
    });
  }

  if (trade.contracts !== null && trade.contracts !== undefined) {
    if (strategyKey === 'acce') {
      stats.push({
        label: 'Contracts',
        value: '4 base',
      });
    } else {
      stats.push({
        label: 'Contracts',
        value: trade.contracts,
      });
    }
  }

  if (trade.status) {
    stats.push({
      label: 'Status',
      value: formatEnumLabel(trade.status),
      tone: trade.status === 'OPEN' ? 'positive' : '',
    });
  }

  return stats;
}

function buildAlarmKey(strategyKey, signal) {
  const trade = signal?.trade;

  return [
    strategyKey,
    signal?.status,
    signal?.trade_title,
    signal?.trade_description,
    signal?.signal_date,
    signal?.entry_date,
    signal?.entry_time,
    getExpirationDate(signal),
    trade?.id,
    trade?.entry_date,
    trade?.entry_time,
    trade?.expiration_date,
    trade?.short_call,
    trade?.long_call,
    trade?.short_put,
    trade?.long_put,
    trade?.entry_credit,
    trade?.entry_debit,
  ]
    .filter(value => value !== null && value !== undefined && value !== '')
    .join('|');
}

function getTradeDescription(strategyKey, signal) {
  const isNoTrade =
    signal?.status === 'NO_TRADE' ||
    signal?.status === 'NO_TRADE_FILTERED';

  if (strategyKey === 'gemic' && isNoTrade) {
    return 'No qualifying GEMIC setup was identified today. GEMIC is standing down.';
  }

  return formatTradeDescription(signal?.trade_description);
}

function normalizeSignal(strategyKey, signal) {
  const meta = STRATEGY_META[strategyKey];

  if (!signal) {
    return {
      key: strategyKey,
      name: meta.name,
      description: meta.description,
      status: 'Waiting',
      rawStatus: null,
      statusType: 'waiting',
      postTime: meta.postTime,
      tradeLabel: 'Next Signal',
      tradeTitle: 'Waiting for signal',
      tradeDescription: `${meta.name} has not posted a signal yet.`,
      pending: true,
      alarmKey: null,
      stats: [],
    };
  }

  return {
    key: strategyKey,
    name: meta.name,
    description: meta.description,
    status: formatStatusLabel(signal.status),
    rawStatus: signal.status,
    statusType: getStatusType(signal.status),
    postTime: meta.postTime,
    tradeLabel: getTradeLabel(signal),
    tradeTitle: signal.trade_title || 'Waiting for signal',
    tradeDescription: getTradeDescription(strategyKey, signal),
    pending:
      signal.status === 'WAITING' ||
      signal.status === 'POSTED_DETAILS_PENDING',
    alarmKey:
      signal.status === 'POSTED'
        ? buildAlarmKey(strategyKey, signal)
        : null,
    stats: buildStats(strategyKey, signal),
  };
}

export default function TradeSignalsSection() {
  const tradeSignals = useStrategyDashboardStore(state => state.tradeSignals);
  const previousPostedAlarmKeysRef = useRef(null);

  const signals = useMemo(
    () =>
      ['argos', 'gemic', 'acce'].map(strategyKey =>
        normalizeSignal(strategyKey, tradeSignals?.[strategyKey])
      ),
    [tradeSignals]
  );

  const postedAlarmKeys = useMemo(
    () => signals.map(signal => signal.alarmKey).filter(Boolean),
    [signals]
  );

  useEffect(() => {
    if (previousPostedAlarmKeysRef.current === null) {
      previousPostedAlarmKeysRef.current = postedAlarmKeys;
      return;
    }

    const newPostedSignals = postedAlarmKeys.filter(
      key => !previousPostedAlarmKeysRef.current.includes(key)
    );

    if (newPostedSignals.length > 0) {
      console.log('[TRADE ALARM] New posted trade signal detected:', newPostedSignals);
      playTradeAlarm();
    }

    previousPostedAlarmKeysRef.current = postedAlarmKeys;
  }, [postedAlarmKeys]);

  return (
    <section className="panel tradeSignalsPanel">
      <div className="panelHeader">
        <div>
          <h2>Trade Signals</h2>
          <p>ARGOS, GEMIC, and ACCE scheduled strategy signals</p>
        </div>
      </div>

      <div className="tradeSignalsGrid">
        {signals.map(signal => (
          <article
            key={signal.key}
            className={`tradeSignalCard ${signal.key}`}
          >
            <div className="tradeSignalHeader">
              <div>
                <h3>{signal.name}</h3>
                <p>{signal.description}</p>
              </div>

              <span className={`signalStatusBadge ${signal.statusType}`}>
                {signal.status}
              </span>
            </div>

            <div className="tradeSignalMeta">
              <span>Posts at</span>
              <strong>{signal.postTime}</strong>
            </div>

            <div className={`tradeBox ${signal.pending ? 'pending' : ''}`}>
              <span>{signal.tradeLabel}</span>
              <strong>{signal.tradeTitle}</strong>
              <p>{signal.tradeDescription}</p>

              {signal.stats?.length > 0 && (
                <div className="tradeStats">
                  {signal.stats.map(stat => (
                    <div key={stat.label}>
                      <span>{stat.label}</span>
                      <strong className={stat.tone || ''}>{stat.value}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}




